import { X } from 'lucide-react';
import { debounce } from 'lodash';
import { useTransition } from 'react';

import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
  SelectLabel,
  SelectSeparator,
} from '@/components/ui/select';
import { InputSearch } from 'src/components/ui/input-search';
import { Button } from 'src/components/ui/button';
import { DepositStatusEnum, SyncStatusEnum } from 'src/graphql/schema';
import { depositStatusOptions, syncStatusOptions } from 'src/containers/Deposits';

const DepositStatusSelect = ({
  depositStatus,
  onChange,
}: {
  depositStatus?: DepositStatusEnum;
  onChange: (value?: DepositStatusEnum) => void;
}) => {
  const onValueChange = (value: string) => onChange(value !== 'all' ? (value as DepositStatusEnum) : undefined);

  return (
    <Select onValueChange={onValueChange} value={depositStatus ?? 'all'}>
      <SelectTrigger className="w-10 sm:w-50 [&>[data-slot=select-value]]:hidden sm:[&>[data-slot=select-value]]:block">
        <SelectValue placeholder="Deposit status" />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="all">All</SelectItem>
        <SelectGroup>
          <SelectSeparator />
          <SelectLabel>Deposit status</SelectLabel>
          {depositStatusOptions.map(({ value, label }) => (
            <SelectItem value={value} key={value}>
              {label}
            </SelectItem>
          ))}
        </SelectGroup>
      </SelectContent>
    </Select>
  );
};

const SyncStatusSelect = ({
  syncStatus,
  onChange,
}: {
  syncStatus?: SyncStatusEnum;
  onChange: (value?: SyncStatusEnum) => void;
}) => {
  const onValueChange = (value: string) => onChange(value !== 'all' ? (value as SyncStatusEnum) : undefined);

  return (
    <Select onValueChange={onValueChange} value={syncStatus ?? 'all'}>
      <SelectTrigger className="w-10 sm:w-50 [&>[data-slot=select-value]]:hidden sm:[&>[data-slot=select-value]]:block">
        <SelectValue placeholder="Sync Status" />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="all">All</SelectItem>
        <SelectGroup>
          <SelectSeparator />
          <SelectLabel>Sync Status</SelectLabel>
          {syncStatusOptions.map(({ value, label }) => (
            <SelectItem value={value} key={value}>
              {label}
            </SelectItem>
          ))}
        </SelectGroup>
      </SelectContent>
    </Select>
  );
};

type Props = {
  depositStatus?: DepositStatusEnum;
  syncStatus?: SyncStatusEnum;
  queryFilter?: string;
  setDepositStatus: (value?: DepositStatusEnum) => void;
  setSyncStatus: (value?: SyncStatusEnum) => void;
  setQueryFilter: (value?: string) => void;
  setInitialFilters: () => void;
};

const AssetsFilters = ({
  depositStatus,
  syncStatus,
  queryFilter,
  setDepositStatus,
  setSyncStatus,
  setQueryFilter,
  setInitialFilters,
}: Props) => {
  const [isPending, startTransition] = useTransition();

  const onChange = debounce((e) => startTransition(() => setQueryFilter(e.target.value)), 500);

  const onClearSearch = () => startTransition(() => setQueryFilter(undefined));

  return (
    <div className="space-y-2">
      <div className="flex gap-2">
        <DepositStatusSelect depositStatus={depositStatus} onChange={setDepositStatus} />
        <SyncStatusSelect syncStatus={syncStatus} onChange={setSyncStatus} />
        <InputSearch
          placeholder="Search"
          className="max-w-[280px] w-full"
          onChange={onChange}
          isLoading={isPending}
          onClear={onClearSearch}
        />
      </div>
      {[depositStatus, syncStatus, queryFilter].some(Boolean) && (
        <Button variant="ghost" className="px-0" onClick={setInitialFilters}>
          <X /> Clear all
        </Button>
      )}
    </div>
  );
};

export default AssetsFilters;
